import type { ReactNode } from 'react';

/**
 * THE TERMS LINE — the one sentence every payment sheet shows under its price
 * (2026-09-23): what the buyer is agreeing to in TIME. On a subscription sheet
 * (Payme card, Octobank) that is the renewal date, the amount and how to stop
 * it; on the one-time sheet it is how long access lasts and that nothing
 * renews.
 *
 * One component, one position, one style, on purpose. The dated three-row
 * boxes it replaced («Оплата и продление», «Оплата и доступ») had drifted: by
 * 23.09 one sat under the card fields, one above the button, one on the
 * one-time sheet in a third layout — three sheets a buyer compares side by
 * side, disclosing the same kind of fact in three places. The owner's call:
 * one style, at the top, since a single line no longer costs the fold
 * (Igor, 2026-09-23). Wording lives with the caller; this only fixes the frame.
 *
 * `headline` (owner, 2026-10-11): the SUBSCRIPTION sheets lead with the refund
 * window — «7 дней — гарантия возврата средств» — in bold, and the renewal
 * sentence drops to a second, quieter line under it. The buyer deciding whether
 * to type a card reads the safety net first and the charge date second; the
 * date is still there, before the card fields, because this is an auto-renewing
 * charge. With a headline the glyph is the refund arrow, not the calendar. The
 * one-time sheet passes no headline and is unchanged.
 */

export function CheckoutTermsLine({
  children,
  headline,
  className = '',
}: {
  children: ReactNode;
  headline?: ReactNode;
  className?: string;
}) {
  return (
    <p
      data-testid="checkout-terms"
      className={`mt-3 flex items-start gap-2 text-[12.5px] leading-snug text-ink-soft ${className}`.trim()}
    >
      {headline ? (
        <svg
          viewBox="0 0 24 24"
          className="mt-[2px] h-[18px] w-[18px] shrink-0 text-brand"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M3 12a9 9 0 1 0 3-6.7" />
          <path d="M3 4v5h5" />
        </svg>
      ) : (
        <svg
          viewBox="0 0 24 24"
          className="mt-[1px] h-4 w-4 shrink-0 text-brand"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 10h18M8 3v4M16 3v4" />
        </svg>
      )}
      {headline ? (
        <span>
          <strong
            data-testid="checkout-terms-headline"
            className="block text-[14.5px] font-bold leading-snug text-ink"
          >
            {headline}
          </strong>
          <span className="mt-0.5 block">{children}</span>
        </span>
      ) : (
        <span>{children}</span>
      )}
    </p>
  );
}
